import React, { useState, useMemo, useEffect } from "react";
import "./metrics.less";
import { Reit, YearData, FinancialMetrics } from "../../../../types/reit.types";
import { safeFormat } from "../../../../utils/financialHelper";

interface MetricsProps {
  reit: Reit | null;
}

interface MetricsRow {
  metric: string;
  values: (string | number)[];
}

const Metrics: React.FC<MetricsProps> = ({ reit }) => {
  const [view, setView] = useState<"Yearly" | "Quarterly">("Yearly");
  const [year, setYear] = useState<string>("");

  const years = useMemo(() => {
    if (!reit?.years) return [];
    return reit.years.map((y: YearData) => `FY ${y.year}`);
  }, [reit]);

  useEffect(() => {
    if (years.length && !year) {
      setYear(years[0]);
    }
  }, [years, year]);

  const metricsData: MetricsRow[] = useMemo(() => {
    if (!reit?.years?.length || !year) return [];

    const yearValue = year.replace("FY ", "");
    const yearData = reit.years.find((y) => y.year === yearValue);

    if (!yearData) return [];

    if (view === "Yearly") {
      const metrics: FinancialMetrics = yearData.financialMetrics;
      return Object.keys(metrics).map((key) => ({
        metric: metrics[key as keyof FinancialMetrics]?.key ?? key,
        values: [safeFormat(metrics[key as keyof FinancialMetrics]?.value)],
      }));
    } else {
      const quarters = (yearData as any).quarters || [];
      if (!quarters.length) return [];

      const keys = Object.keys(
        (quarters[0]?.financialMetrics as FinancialMetrics) || {}
      );

      return keys.map((key) => ({
        metric:
          quarters[0]?.financialMetrics?.[key as keyof FinancialMetrics]?.key ??
          key,
        values: quarters.map((q: { financialMetrics: FinancialMetrics }) =>
          safeFormat(q.financialMetrics?.[key as keyof FinancialMetrics]?.value)
        ),
      }));
    }
  }, [reit, year, view]);

  return (
    <section className="metrics">
      <div className="metrics__card">
        {/* Header */}
        <div className="metrics__header">
          <h2>Operational Metrics</h2>

          <div className="metrics__subheader">
            {/* Toggle */}
            <div className="metrics__tabs">
              <button
                className={view === "Yearly" ? "active" : ""}
                onClick={() => setView("Yearly")}
              >
                Yearly
              </button>
              <button
                className={view === "Quarterly" ? "active" : ""}
                onClick={() => setView("Quarterly")}
              >
                Quarterly
              </button>
            </div>

            {/* Dropdown */}
            <div className="metrics__dropdown">
              <div className="custom-select">
                <select value={year} onChange={(e) => setYear(e.target.value)}>
                  {years.map((y, idx) => (
                    <option key={idx} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="metrics__table">
          <table>
            <thead>
              <tr>
                <th>Parameters</th>
                {view === "Quarterly" ? (
                  <>
                    <th>Q1</th>
                    <th>Q2</th>
                    <th>Q3</th>
                    <th>Q4</th>
                  </>
                ) : (
                  <th>Value</th>
                )}
              </tr>
            </thead>
            <tbody>
              {metricsData.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.metric}</td>
                  {row.values.map((val, i) => (
                    <td key={i}>{val}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default Metrics;
